import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePopupPrefill } from "../ai-chat/aiPopupGateway";
import {
  LuCheck,
  LuChevronDown,
  LuChevronUp,
  LuFileText,
  LuImage,
  LuLightbulb,
  LuPenLine,
  LuSparkles,
} from "react-icons/lu";
import i18n from "i18next";
import ImagePromptLibraryService from "../../services/imagePromptLibraryService";
import { approveJob } from "../../../common/server_utils.es6";
import { getToken } from "../../../common/environment_config.es6";
import { isFileTooLarge, fileTooLargeMessage } from "../../../common/upload_limits.es6";
import { mapAiProviderErrorMessage } from "../../../common/ai_provider_errors.es6";
import {
  IMAGE_STYLE_PROVIDER_OPTIONS,
  VISIBLE_IMAGE_STYLE_PROVIDER_OPTIONS,
} from "../image_style_library/imageStyleProviderOptions";
import { ImageAnimatorModal, IMAGE_EDIT_FEATURES } from "../merlin/ImageAnimatorModal";
import { onJobsSettled, getSettledOutcome, SETTLED_APPROVED } from "../../utils/jobSettled";
import MerlinFeaturesPanel from "../merlin/MerlinFeaturesPanel";
import InsufficientBalanceModal from "../common/modal/InsufficientBalanceModal";
import {
  notifyInsufficientBalance,
  CLOSED_CREDIT_REFUSAL,
  CREDIT_REFUSAL,
  creditRefusalKind,
  creditRefusalState,
  isCreditRefusal,
} from "../../utils/insufficientBalance";

// "aidea Smart Elements" dispatches a Redux thunk against a canvas box (dispatchDirect)
// and has no meaning yet for a freshly generated image that hasn't been placed on a
// scene — exclude it from the Aidea Wizard AI picker shown here.
const AI_TAB_MERLIN_FEATURES = IMAGE_EDIT_FEATURES.filter((feature) => !feature.dispatchDirect);

const TEXT_LOCATIONS = [
  { value: "top left", label: i18n.t("TopLeft") || "Top Left" },
  { value: "top center", label: i18n.t("TopCenter") || "Top Center" },
  { value: "top right", label: i18n.t("TopRight") || "Top Right" },
  { value: "middle left", label: i18n.t("BottomLeft") || "Bottom Left" },
  { value: "middle center", label: i18n.t("Center") || "Center" },
  { value: "middle right", label: i18n.t("BottomRight") || "Bottom Right" },
  { value: "bottom left", label: i18n.t("BottomLeft") || "Bottom Left" },
  { value: "bottom center", label: i18n.t("BottomCenter") || "Bottom Center" },
  { value: "bottom right", label: i18n.t("BottomRight") || "Bottom Right" },
];

const TEXT_STYLES = [
  {
    value: "bold white with shadow",
    label: i18n.t("BoldWhiteWithShadow") || "Bold White (Shadow)",
  },
  {
    value: "bold black with shadow",
    label: i18n.t("BoldBlackWithShadow") || "Bold Black (Shadow)",
  },
  { value: "large white bold", label: i18n.t("LargeWhiteBold") || "Large White Bold" },
  { value: "medium white regular", label: i18n.t("MediumWhiteRegular") || "Medium White Regular" },
  { value: "small white italic", label: i18n.t("SmallWhiteItalic") || "Small White Italic" },
  { value: "small gray italic", label: i18n.t("SmallGrayItalic") || "Small Gray Italic" },
  {
    value: "bold yellow with shadow",
    label: i18n.t("BoldYellowWithShadow") || "Bold Yellow (Shadow)",
  },
  { value: "custom", label: i18n.t("Custom") || "Custom..." },
];

const ASPECT_RATIOS = [
  { id: "16:9", w: 16, h: 9 },
  { id: "9:16", w: 9, h: 16 },
  { id: "1:1", w: 1, h: 1 },
  { id: "4:3", w: 4, h: 3 },
];

const CUSTOM_STYLE_ID = "__custom__";
const AUTO_PROVIDER = "gemini-pro-nano";
const PROVIDER_API_ALIAS = {};
const resolveApiProvider = (provider) => PROVIDER_API_ALIAS[provider] || provider;
const RESOLUTION_SUPPORTED_PROVIDERS = ["gemini-nano", "gemini-pro-nano"];
const providerSupportsResolution = (provider) => RESOLUTION_SUPPORTED_PROVIDERS.includes(provider);
export { CUSTOM_STYLE_ID };
const AIImageGenerateTab = ({
  aspectRatio,
  onAspectRatioChange,
  model,
  onModelChange,
  narrationText = "",
  onGenerateReady,
  onResetReady,
  onCreditCheckReady,
  onServerModal,
  trainingId = null,
  onAddToScene,
  customStyleDesc = "",
  onCustomStyleDescChange,
  onStateChange,
  onInsufficientBalance,
  sceneIndex = null,
  navId = null,
}) => {
  const [styles, setStyles] = useState([]);
  const [stylesLoading, setStylesLoading] = useState(false);
  const [stylesError, setStylesError] = useState("");
  const [selectedStyle, setSelectedStyle] = useState(null);
  // An agent-picked style that arrived BEFORE the style list finished loading.
  // The styles-load effect applies it (and skips its default selection) so the
  // agent's choice isn't lost to a slow fetch or overridden by the default.
  const pendingAgentStyleRef = useRef(null);
  const [showAllStyles, setShowAllStyles] = useState(false);
  const [textInImage, setTextInImage] = useState(false);
  const [textOverlays, setTextOverlays] = useState([
    {
      id: 1,
      text: "",
      location: "top center",
      customLocation: "",
      style: "bold white with shadow",
      customStyle: "",
    },
  ]);
  const [promptMode, setPromptMode] = useState("write");
  const [writePrompt, setWritePrompt] = useState("");
  const [narrationPrompt, setNarrationPrompt] = useState("");
  const [directPrompt, setDirectPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [resolution, setResolution] = useState("1K");
  const [isEnrichingPrompt, setIsEnrichingPrompt] = useState(false);
  const referenceFileInputRef = useRef(null);
  const [referenceImage, setReferenceImage] = useState(null);
  const [referenceImagePreview, setReferenceImagePreview] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [providerResults, setProviderResults] = useState({});
  // Sonucun işi onaylandıysa (iş tepsisinden — tepsi görseli sahneye zaten
  // ekledi — ya da buradan) "Sahneye Ekle" sessizce ikinci kopya eklerdi →
  // durumu göster, buton "Tekrar ekle" olsun. Sinyal gelince yeniden çiz.
  const [, setSettledTick] = useState(0);
  useEffect(() => onJobsSettled(() => setSettledTick((t) => t + 1)), []);
  const [isGeneratingFromNarration, setIsGeneratingFromNarration] = useState(false);
  const [isAutoProvider, setIsAutoProvider] = useState(true);
  const [selectedProviders, setSelectedProviders] = useState([AUTO_PROVIDER]);
  const [editorOpenFor, setEditorOpenFor] = useState(null);
  // Feature the user picked from the Aidea Wizard AI list, per provider — once set,
  // the editor opens directly on that tool instead of showing the picker.
  const [merlinFeatureFor, setMerlinFeatureFor] = useState({});
  const [insufficientBalance, setInsufficientBalance] = useState(CLOSED_CREDIT_REFUSAL);

  // Detects a credit refusal (org balance or team budget) in image-gen errors.
  // If onInsufficientBalance prop is provided, delegates to parent (so parent can
  // close its own modal etc.) and skips the internal popup. Otherwise opens the
  // internal popup as a self-contained fallback.
  const checkInsufficientBalance = useCallback(
    (errorStr, errData) => {
      if (!isCreditRefusal(errorStr, errData)) return false;
      // The reason may be only in errorStr; carry it so the parent's popup
      // can still tell a team budget from an empty org balance.
      const payload = { ...(errData || {}), message: errData?.message || errorStr };
      if (typeof onInsufficientBalance === "function") {
        onInsufficientBalance(payload);
        return true;
      }
      setInsufficientBalance(creditRefusalState(payload));
      return true;
    },
    [onInsufficientBalance]
  );

  const selectAutoMode = () => {
    if (isAutoProvider) return;
    setIsAutoProvider(true);
    onModelChange(AUTO_PROVIDER);
  };

  const selectCustomMode = () => {
    if (!isAutoProvider) return;
    setIsAutoProvider(false);
  };

  const toggleProvider = (value) => {
    setSelectedProviders((prev) => {
      if (prev.includes(value)) {
        return prev.length > 1 ? prev.filter((v) => v !== value) : prev;
      }
      return [...prev, value];
    });
  };

  const hasAnyResult = Object.keys(providerResults).length > 0;

  const activeProviders = isAutoProvider ? [AUTO_PROVIDER] : selectedProviders;
  // Çözünürlük kartı her zaman görünür kalır; ancak yalnızca seçili TÜM sağlayıcılar
  // destekliyorsa (Nano Banana 2 / Nano Banana Pro) seçilebilir. Aksi halde (örn.
  // Flux + Nano Banana 2) görünür ama devre dışı (seçilemez) durumda gösterilir.
  const resolutionSelectable =
    activeProviders.length > 0 && activeProviders.every(providerSupportsResolution);
  const activeProviderLabel =
    activeProviders.length === 1
      ? IMAGE_STYLE_PROVIDER_OPTIONS.find((o) => o.value === activeProviders[0])?.label ||
        activeProviders[0]
      : `${activeProviders.length} ${i18n.t("FileModal.APIProviders.ImageStyles.ImagesGenerating") || "görsel üretiliyor"}`;

  // Notify parent about state changes
  useEffect(() => {
    if (onStateChange) {
      onStateChange({ isGenerating, isGeneratingFromNarration, hasResult: hasAnyResult });
    }
  }, [isGenerating, isGeneratingFromNarration, hasAnyResult, onStateChange]);
  const hasReferenceImage = !!referenceImage;
  const isCustomStyle = selectedStyle === CUSTOM_STYLE_ID;
  const selectedStyleData = isCustomStyle
    ? null
    : styles.find((style) => style.id === selectedStyle);
  const selectedStyleLabel = hasReferenceImage
    ? i18n.t("FileModal.APIProviders.VideoGeneration.StyleReference") || "Style Reference"
    : isCustomStyle
      ? i18n.t("CustomStyle") || "Custom"
      : selectedStyleData?.name || selectedStyleData?.promptText || i18n.t("Style") || "Style";

  // Current prompt based on mode
  const prompt =
    promptMode === "write"
      ? writePrompt
      : promptMode === "narration"
        ? narrationPrompt
        : directPrompt;
  const setPrompt =
    promptMode === "write"
      ? setWritePrompt
      : promptMode === "narration"
        ? setNarrationPrompt
        : setDirectPrompt;
  const isDirectMode = promptMode === "direct";

  useEffect(() => {
    let isCancelled = false;

    const loadStyles = async () => {
      setStylesLoading(true);
      setStylesError("");
      try {
        const data = await ImagePromptLibraryService.getImagePromptLibrary({
          limit: 20,
          offset: 0,
          orderBy: "createdAt",
          orderDirection: "DESC",
        });
        if (!isCancelled) {
          const items = (data?.libraries || data?.items || data || []).filter(
            (item) => item.isActive !== false
          );
          setStyles(items);
          // An agent-picked style that the prefill couldn't resolve before the
          // list loaded takes priority over the default selection below.
          const pend = pendingAgentStyleRef.current;
          let appliedPending = false;
          if (pend && items.length) {
            let idx = -1;
            if (pend.styleId != null) idx = items.findIndex((st) => st.id === pend.styleId);
            if (idx < 0 && pend.style) {
              idx = items.findIndex(
                (st) =>
                  (st?.name || st?.promptText || "").toLowerCase() ===
                  String(pend.style).toLowerCase()
              );
            }
            if (idx >= 0) {
              setSelectedStyle(items[idx].id);
              if (idx >= 5) setShowAllStyles(true);
              appliedPending = true;
            }
            pendingAgentStyleRef.current = null;
          }
          if (!appliedPending && items.length > 0 && !selectedStyle) {
            const savedLibraryId = localStorage.getItem("imagePromptLibraryId");
            if (savedLibraryId) {
              const savedIdNum = parseInt(savedLibraryId, 10);
              const savedStyle = items.find((item) => item.id === savedIdNum);
              if (savedStyle) {
                setSelectedStyle(savedIdNum);
              } else {
                setSelectedStyle(items[0].id);
              }
            } else {
              setSelectedStyle(items[0].id);
            }
          }
        }
      } catch (error) {
        if (!isCancelled) {
          setStylesError(i18n.t("styles_load_error") || "Failed to load style templates");
          setStyles([]);
        }
      } finally {
        if (!isCancelled) {
          setStylesLoading(false);
        }
      }
    };

    loadStyles();

    return () => {
      isCancelled = true;
    };
  }, []);

  const resolveStyleLabel = useCallback((style, index) => {
    if (style?.translationKey) {
      const translated = i18n.t(style.translationKey);
      if (translated && translated !== style.translationKey) return translated;
    }

    return (
      style?.localizedName ||
      style?.label ||
      style?.name ||
      style?.promptText ||
      i18n.t("FileModal.APIProviders.ImageStyles.StyleTemplateFallback", { index }) ||
      `Style ${index}`
    );
  }, []);

  const resolveOrientation = () => {
    if (aspectRatio === "9:16") return "portrait";
    if (aspectRatio === "1:1") return "square";
    return "landscape";
  };

  const addOverlay = () => {
    setTextOverlays((prev) => [
      ...prev,
      {
        id: Date.now(),
        text: "",
        location: "top center",
        customLocation: "",
        style: "bold white with shadow",
        customStyle: "",
      },
    ]);
  };

  const removeOverlay = (id) => {
    setTextOverlays((prev) => prev.filter((o) => o.id !== id));
  };

  const updateOverlay = (id, field, value) => {
    setTextOverlays((prev) => prev.map((o) => (o.id === id ? { ...o, [field]: value } : o)));
  };

  const handleReferenceImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith("image/")) return;
    // 500 MB üst sınırı: aşan dosyayı kabul etme.
    if (isFileTooLarge(file)) {
      if (onServerModal) {
        onServerModal(i18n.t("FileModal.APIProviders.error"), fileTooLargeMessage(), false);
      }
      if (referenceFileInputRef.current) referenceFileInputRef.current.value = "";
      return;
    }
    setReferenceImage(file);
    const reader = new FileReader();
    reader.onload = (ev) => setReferenceImagePreview(ev.target.result);
    reader.readAsDataURL(file);
    setSelectedStyle(null);
  };

  const clearReferenceImage = () => {
    setReferenceImage(null);
    setReferenceImagePreview("");
    if (referenceFileInputRef.current) referenceFileInputRef.current.value = "";
  };

  const handleEnrichPrompt = async () => {
    const currentPrompt = promptMode === "direct" ? directPrompt : writePrompt;
    const applyEnriched = promptMode === "direct" ? setDirectPrompt : setWritePrompt;
    if (!currentPrompt.trim()) {
      if (onServerModal) {
        onServerModal(
          i18n.t("FileModal.APIProviders.ImageStyles.PromptRequiredTitle"),
          i18n.t("FileModal.APIProviders.ImageStyles.PromptEnrichEmpty") ||
            "Please enter a prompt to enrich",
          false
        );
      }
      return;
    }

    setIsEnrichingPrompt(true);
    try {
      const enrichedPrompt = await ImagePromptLibraryService.enrichPrompt(currentPrompt.trim());
      applyEnriched(enrichedPrompt);
    } catch (error) {
      if (onServerModal) {
        onServerModal(
          i18n.t("FileModal.APIProviders.error"),
          `${
            i18n.t("FileModal.APIProviders.ImageStyles.PromptEnrichFailed") ||
            "Failed to enrich prompt"
          }: ${error.message || i18n.t("UnknownError") || "Unknown error"}`,
          false
        );
      }
    } finally {
      setIsEnrichingPrompt(false);
    }
  };

  const handleGeneratePromptFromNarration = async () => {
    if (!narrationPrompt.trim()) {
      if (onServerModal) {
        onServerModal(
          i18n.t("FileModal.APIProviders.ImageStyles.NarrationRequiredTitle"),
          i18n.t("FileModal.APIProviders.ImageStyles.NarrationTextRequired") ||
            "Please enter narration text",
          false
        );
      }
      return;
    }

    setIsGeneratingFromNarration(true);
    try {
      const generatedPrompt = await ImagePromptLibraryService.generatePromptFromNarration({
        narrationText: narrationPrompt.trim(),
        idealStudioTrainingId: trainingId || undefined,
      });

      setNarrationPrompt(generatedPrompt);
    } catch (error) {
      if (onServerModal) {
        onServerModal(
          i18n.t("FileModal.APIProviders.error"),
          `${
            i18n.t("FileModal.APIProviders.ImageStyles.PromptGenerationFailed") ||
            "Failed to generate prompt from narration"
          }: ${error.message || i18n.t("UnknownError") || "Unknown error"}`,
          false
        );
      }
    } finally {
      setIsGeneratingFromNarration(false);
    }
  };

  const handleError = (errorStr) => {
    if (onServerModal) {
      onServerModal(
        i18n.t("FileModal.APIProviders.error"),
        `${i18n.t("FileModal.APIProviders.ImageStyles.FailedToGenerate")}: ${
          errorStr || i18n.t("UnknownError") || "Unknown error"
        }`,
        false
      );
    }
  };

  const fireProviderRequest = useCallback(
    (provider, currentPrompt, activeOverlays, isCustom) => {
      if (referenceImage) {
        ImagePromptLibraryService.generateImageWithReference(
          {
            image: referenceImage,
            provider: resolveApiProvider(provider),
            orientation: resolveOrientation(),
            prompt: currentPrompt,
            ...(activeOverlays.length > 0 && { textOverlays: activeOverlays }),
            ...(providerSupportsResolution(provider) && resolution && { resolution }),
            ...(trainingId && { idealStudioTrainingId: trainingId }),
            ...(sceneIndex != null && { sceneIndex }),
          },
          (isSuccess, errorStr, fileUrl, errData, jobId) => {
            if (!isSuccess) checkInsufficientBalance(errorStr, errData);
            setProviderResults((prev) => ({
              ...prev,
              [provider]: isSuccess
                ? { status: "done", url: fileUrl, jobId, error: null }
                : { status: "error", url: null, error: errorStr },
            }));
          }
        );
        return;
      }

      if (textInImage && activeOverlays.length > 0) {
        ImagePromptLibraryService.generateImageWithText(
          {
            prompt: currentPrompt,
            provider: resolveApiProvider(provider),
            orientation: resolveOrientation(),
            textOverlays: activeOverlays,
            ...(isCustom && customStyleDesc.trim() && { customStyle: customStyleDesc.trim() }),
            ...(negativePrompt.trim() && { negativePrompt: negativePrompt.trim() }),
            ...(providerSupportsResolution(provider) && resolution && { resolution }),
            imagePromptLibraryId: !isCustom && selectedStyleData ? selectedStyleData.id : null,
            ...(trainingId && { idealStudioTrainingId: trainingId }),
          },
          (isSuccess, errorStr, fileUrl, errData, jobId) => {
            if (!isSuccess) checkInsufficientBalance(errorStr, errData);
            setProviderResults((prev) => ({
              ...prev,
              [provider]: isSuccess
                ? { status: "done", url: fileUrl, jobId, error: null }
                : { status: "error", url: null, error: errorStr },
            }));
          }
        );
        return;
      }

      ImagePromptLibraryService.generateImageWithStyle(
        {
          prompt: currentPrompt,
          provider: resolveApiProvider(provider),
          orientation: resolveOrientation(),
          imageCount: 1,
          quality: "hd",
          ...(isCustom && customStyleDesc.trim() && { customStyle: customStyleDesc.trim() }),
          ...(negativePrompt.trim() && { negativePrompt: negativePrompt.trim() }),
          ...(providerSupportsResolution(provider) && resolution && { resolution }),
          imagePromptLibraryId: !isCustom && selectedStyleData ? selectedStyleData.id : null,
          ...(trainingId && { idealStudioTrainingId: trainingId }),
          ...(sceneIndex != null && { sceneIndex }),
        },
        (isSuccess, errorStr, fileUrl, errData, jobId) => {
          if (!isSuccess) checkInsufficientBalance(errorStr, errData);
          setProviderResults((prev) => ({
            ...prev,
            [provider]: isSuccess
              ? { status: "done", url: fileUrl, jobId, error: null }
              : { status: "error", url: null, error: errorStr },
          }));
        }
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      referenceImage,
      textInImage,
      customStyleDesc,
      negativePrompt,
      resolution,
      selectedStyleData,
      trainingId,
      aspectRatio,
      checkInsufficientBalance,
      sceneIndex,
    ]
  );

  const generateImage = useCallback(() => {
    const currentPrompt =
      promptMode === "write"
        ? writePrompt
        : promptMode === "narration"
          ? narrationPrompt
          : directPrompt;

    if (promptMode === "direct") {
      const resolvedPrompt = currentPrompt.trim();
      if (!resolvedPrompt) {
        if (onServerModal) {
          onServerModal(
            i18n.t("FileModal.APIProviders.ImageStyles.PromptRequiredTitle"),
            i18n.t("FileModal.APIProviders.ImageStyles.PleaseEnterPrompt"),
            false
          );
        }
        return;
      }

      const providers = isAutoProvider ? [AUTO_PROVIDER] : selectedProviders;
      const initialResults = {};
      providers.forEach((p) => {
        initialResults[p] = { status: "loading", url: null, error: null };
      });
      setProviderResults(initialResults);
      setIsGenerating(true);

      providers.forEach((provider) => {
        ImagePromptLibraryService.generateImageDirect(
          {
            prompt: resolvedPrompt,
            provider: resolveApiProvider(provider),
            improvePrompt: false,
            orientation: resolveOrientation(),
            ...(providerSupportsResolution(provider) && resolution && { resolution }),
            ...(sceneIndex != null && { sceneIndex }),
          },
          (isSuccess, errorStr, fileUrl, errData, jobId) => {
            if (!isSuccess) checkInsufficientBalance(errorStr, errData);
            setProviderResults((prev) => ({
              ...prev,
              [provider]: isSuccess
                ? { status: "done", url: fileUrl, jobId, error: null }
                : { status: "error", url: null, error: errorStr },
            }));
          }
        );
      });
      return;
    }

    const isCustom = selectedStyle === CUSTOM_STYLE_ID;
    const resolvedPrompt = currentPrompt.trim();

    // 1) Prompt her zaman gerekli
    if (!resolvedPrompt) {
      if (onServerModal) {
        onServerModal(
          i18n.t("FileModal.APIProviders.ImageStyles.PromptRequiredTitle"),
          i18n.t("FileModal.APIProviders.ImageStyles.PromptRequired") || "Please enter a prompt.",
          false
        );
      }
      return;
    }

    // 2) Stil seçimi gerekli (referans görsel veya özel stil seçilmediyse)
    if (!referenceImage && !isCustom && !selectedStyleData) {
      if (onServerModal) {
        onServerModal(
          i18n.t("FileModal.APIProviders.ImageStyles.StyleRequiredTitle") ||
            i18n.t("FileModal.APIProviders.ImageStyles.PromptRequiredTitle"),
          i18n.t("FileModal.APIProviders.ImageStyles.PleaseSelectStyle") ||
            "Please select a style or add a reference image.",
          false
        );
      }
      return;
    }

    // 3) Özel stil seçildiyse açıklama gerekli
    if (isCustom && !customStyleDesc.trim()) {
      if (onServerModal) {
        onServerModal(
          i18n.t("FileModal.APIProviders.ImageStyles.CustomStyleRequiredTitle") ||
            i18n.t("FileModal.APIProviders.ImageStyles.PromptRequiredTitle"),
          i18n.t("FileModal.APIProviders.ImageStyles.CustomStyleDescRequired") ||
            "Please enter a description for the custom style.",
          false
        );
      }
      return;
    }

    const activeOverlays = textInImage
      ? textOverlays
          .filter((o) => o.text.trim())
          .map(({ text, location, customLocation, style, customStyle }) => ({
            text,
            location: location === "custom" ? customLocation || "center" : location,
            style: style === "custom" ? customStyle || "bold white" : style,
          }))
      : [];

    const providers = isAutoProvider ? [AUTO_PROVIDER] : selectedProviders;
    const initialResults = {};
    providers.forEach((p) => {
      initialResults[p] = { status: "loading", url: null, error: null };
    });
    setProviderResults(initialResults);
    setIsGenerating(true);

    providers.forEach((provider) => {
      fireProviderRequest(provider, resolvedPrompt, activeOverlays, isCustom);
    });
  }, [
    writePrompt,
    narrationPrompt,
    directPrompt,
    promptMode,
    referenceImage,
    aspectRatio,
    selectedStyle,
    selectedStyleData,
    customStyleDesc,
    onServerModal,
    textInImage,
    textOverlays,
    trainingId,
    isAutoProvider,
    selectedProviders,
    resolution,
    fireProviderRequest,
    sceneIndex,
  ]);

  useEffect(() => {
    if (typeof onGenerateReady === "function") {
      onGenerateReady(generateImage);
    }
  }, [onGenerateReady, generateImage]);

  // One request per provider in parallel — different providers may have different
  // pricing, so we fetch each separately and sum the results.
  const checkCredit = useCallback(async () => {
    const providers = isAutoProvider ? [AUTO_PROVIDER] : selectedProviders;
    const orientation =
      aspectRatio === "9:16" ? "portrait" : aspectRatio === "1:1" ? "square" : "landscape";
    const apiUrl = process.env.MW_API_URL;

    const getProviderLabel = (value) => {
      const opt = IMAGE_STYLE_PROVIDER_OPTIONS.find((o) => o.value === value);
      return opt ? opt.label : value;
    };

    const fetchCredit = async (provider) => {
      const body = {
        prompt: "credit-estimate",
        provider: resolveApiProvider(provider),
        imageCount: 1,
        orientation,
        ...(providerSupportsResolution(provider) && resolution ? { resolution } : {}),
      };
      const res = await fetch(`${apiUrl}api/ideal-studio/text-to-image-async/credit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || "Credit check failed");
      }
      return res.json();
    };

    const results = await Promise.all(providers.map(fetchCredit));

    const availableCredits = results[0].availableCredits;
    const providerBreakdown = providers.map((p, i) => ({
      label: getProviderLabel(p),
      cost: Number(results[i].requiredCredits.toFixed(4)),
    }));
    const totalRequired = Number(providerBreakdown.reduce((sum, p) => sum + p.cost, 0).toFixed(4));

    return {
      requiredCredits: totalRequired,
      availableCredits,
      hasSufficientBalance: availableCredits >= totalRequired,
      providers: providerBreakdown,
    };
  }, [isAutoProvider, selectedProviders, aspectRatio, resolution]);

  useEffect(() => {
    if (typeof onCreditCheckReady === "function") {
      onCreditCheckReady(checkCredit);
    }
  }, [onCreditCheckReady, checkCredit]);

  // Apply the agent's intent: prompt → Write mode; style name → selected style id;
  // aspectRatio → bubble to the parent (aspect is a parent-owned prop). Best-effort.
  const applyImageIntent = useCallback(
    ({ prompt, settings }) => {
      if (typeof prompt === "string" && prompt.trim()) {
        setPromptMode("write");
        setWritePrompt(prompt);
      }
      const s = settings || {};
      let chosenIndex = -1;
      let resolved = false;
      if (s.styleId != null && Array.isArray(styles)) {
        chosenIndex = styles.findIndex((st) => st.id === s.styleId);
        if (chosenIndex >= 0) {
          setSelectedStyle(s.styleId);
          resolved = true;
        }
      }
      if (!resolved && s.style && Array.isArray(styles) && styles.length) {
        chosenIndex = styles.findIndex(
          (st) => (st?.name || st?.promptText || "").toLowerCase() === String(s.style).toLowerCase()
        );
        if (chosenIndex >= 0) {
          setSelectedStyle(styles[chosenIndex].id);
          resolved = true;
        }
      }
      // Couldn't resolve yet (style list not loaded) — remember it so the
      // styles-load effect applies it instead of the default.
      if (!resolved && (s.styleId != null || s.style)) {
        pendingAgentStyleRef.current = { styleId: s.styleId, style: s.style };
      } else if (resolved) {
        pendingAgentStyleRef.current = null;
      }
      // The style row only shows the first 5 unless expanded. If the agent picked
      // a style hidden in the "More styles" menu, expand so the user sees it
      // selected (otherwise it looks like nothing is selected).
      if (chosenIndex >= 5) setShowAllStyles(true);
      if (s.aspectRatio && typeof onAspectRatioChange === "function") {
        onAspectRatioChange(s.aspectRatio);
      }
    },
    [styles, onAspectRatioChange]
  );

  usePopupPrefill("image", applyImageIntent);

  const regenerateImage = useCallback(() => {
    generateImage();
  }, [generateImage]);

  // Sonuç görünümünden form'a dönmek için: sadece providerResults + editör
  // alt-görünümü temizleniyor; prompt, stil, provider seçimi, aspect ratio
  // vb. form state'leri korunuyor.
  const clearResults = useCallback(() => {
    setProviderResults({});
    setIsGenerating(false);
    setEditorOpenFor(null);
    setMerlinFeatureFor({});
  }, []);

  useEffect(() => {
    if (typeof onResetReady === "function") {
      onResetReady(clearResults);
    }
  }, [onResetReady, clearResults]);

  // Turn off initial isGenerating once first result arrives
  useEffect(() => {
    if (isGenerating && hasAnyResult) {
      setIsGenerating(false);
    }
  }, [isGenerating, hasAnyResult]);

  const renderInsufficientBalanceModal = () => (
    <InsufficientBalanceModal
      show={insufficientBalance.show}
      onClose={() => setInsufficientBalance((prev) => ({ ...prev, show: false }))}
      requestedAmount={insufficientBalance.requestedAmount}
      currentBalance={insufficientBalance.currentBalance}
      kind={insufficientBalance.kind}
      teamName={insufficientBalance.teamName}
      resetsAt={insufficientBalance.resetsAt}
    />
  );

  if (isGenerating && !hasAnyResult) {
    return (
      <>
        <div className="add-image-modal__ai-tab">
          <div className="add-image-modal__ai-generating">
            <div className="add-image-modal__ai-generating-spinner" />
            <div className="add-image-modal__ai-generating-label">
              {i18n.t("FileModal.APIProviders.ImageStyles.GeneratingImage") ||
                "Generating image..."}
            </div>
            <div className="add-image-modal__ai-generating-meta">
              {selectedStyleLabel} · {aspectRatio} · {activeProviderLabel}
            </div>
          </div>
        </div>
        {renderInsufficientBalanceModal()}
      </>
    );
  }

  if (hasAnyResult) {
    const resultEntries = Object.entries(providerResults);
    const isSingle = resultEntries.length === 1;
    return (
      <>
        <div className="add-image-modal__ai-tab">
          <div
            className={`add-image-modal__ai-results-grid${isSingle ? " add-image-modal__ai-results-grid--single" : ""}`}
          >
            {resultEntries.map(([provider, result]) => {
              const providerLabel =
                IMAGE_STYLE_PROVIDER_OPTIONS.find((o) => o.value === provider)?.label || provider;
              return (
                <div key={provider} className="add-image-modal__ai-result-card">
                  <div className="add-image-modal__ai-result-card-header">{providerLabel}</div>
                  {result.status === "loading" && (
                    <div className="add-image-modal__ai-result-card-loading">
                      <div className="add-image-modal__ai-generating-spinner" />
                    </div>
                  )}
                  {result.status === "done" && result.url && (
                    <>
                      <div className="add-image-modal__ai-result-card-img-wrap">
                        <img
                          className="add-image-modal__ai-result-card-img"
                          src={result.url}
                          alt={providerLabel}
                        />
                      </div>
                      {getSettledOutcome(result.jobId) === SETTLED_APPROVED && (
                        <div
                          style={{
                            fontSize: 12,
                            color: "#16a34a",
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "4px 0",
                          }}
                        >
                          <LuCheck size={12} />
                          {i18n.t("AddedToScene")}
                        </div>
                      )}
                      <div className="add-image-modal__ai-result-card-actions">
                        {onAddToScene && (
                          <button
                            type="button"
                            className="add-image-modal__ai-result-btn add-image-modal__ai-result-btn--ghost"
                            onClick={() => {
                              onAddToScene(
                                result.url,
                                i18n.t("FileModal.APIProviders.ImageStyles.AIGeneratedImage") ||
                                  "AI Generated Image"
                              );
                              // Sahneye eklendi → widget'taki "Onay Bekliyor" kartını kapat.
                              approveJob(result.jobId);
                            }}
                          >
                            {getSettledOutcome(result.jobId) === SETTLED_APPROVED
                              ? i18n.t("AddAgain")
                              : i18n.t("AddToScene") || "+ Sahneye Ekle"}
                          </button>
                        )}
                        <button
                          type="button"
                          className="add-image-modal__ai-result-btn add-image-modal__ai-result-btn--primary"
                          onClick={() =>
                            setEditorOpenFor((prev) => {
                              const opening = prev !== provider;
                              if (!opening) {
                                setMerlinFeatureFor((f) => {
                                  const { [provider]: _drop, ...rest } = f;
                                  return rest;
                                });
                              }
                              return opening ? provider : null;
                            })
                          }
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            padding: "12px 20px",
                            borderRadius: "8px",
                            fontWeight: "500",
                            fontSize: "14px",
                            letterSpacing: "0.01em",
                            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                            boxShadow:
                              editorOpenFor === provider
                                ? "0 2px 8px rgba(0, 0, 0, 0.15)"
                                : "0 2px 6px rgba(0, 0, 0, 0.1)",
                            transform: editorOpenFor === provider ? "translateY(-1px)" : "none",
                          }}
                        >
                          {editorOpenFor === provider ? (
                            <>
                              <LuChevronUp size={16} />
                              {i18n.t("Collapse") || "Daralt"}
                            </>
                          ) : (
                            <>
                              <LuSparkles size={16} />
                              {i18n.t("MerlinAI") || "Aidea Wizard AI"}
                            </>
                          )}
                        </button>
                      </div>
                      {editorOpenFor === provider &&
                        (merlinFeatureFor[provider] ? (
                          <div className="add-image-modal__ai-merlin-wrap">
                            <button
                              type="button"
                              className="add-image-modal__ai-merlin-back"
                              onClick={() =>
                                setMerlinFeatureFor((prev) => {
                                  const { [provider]: _drop, ...rest } = prev;
                                  return rest;
                                })
                              }
                            >
                              ‹ {i18n.t("ChangeTool") || "Araç Değiştir"}
                            </button>
                            <ImageAnimatorModal
                              embed
                              visible
                              imageUrl={result.url}
                              navId={navId}
                              sceneIndex={sceneIndex}
                              initialFeatureId={merlinFeatureFor[provider]}
                              onClose={() => setEditorOpenFor(null)}
                              onAddToScene={(newUrl) => {
                                setProviderResults((prev) => ({
                                  ...prev,
                                  [provider]: { ...prev[provider], url: newUrl },
                                }));
                                setEditorOpenFor(null);
                                setMerlinFeatureFor((prev) => {
                                  const { [provider]: _drop, ...rest } = prev;
                                  return rest;
                                });
                              }}
                              onInsufficientBalance={notifyInsufficientBalance}
                            />
                          </div>
                        ) : (
                          <div className="add-image-modal__ai-merlin-launcher">
                            <MerlinFeaturesPanel
                              features={AI_TAB_MERLIN_FEATURES}
                              onSelectFeature={(featureId) => {
                                setMerlinFeatureFor((prev) => ({ ...prev, [provider]: featureId }));
                              }}
                            />
                          </div>
                        ))}
                    </>
                  )}
                  {result.status === "error" && (
                    <div className="add-image-modal__ai-result-card-error">
                      {result.error
                        ? (() => {
                            const errorStr = String(result.error);
                            const refusal = creditRefusalKind(errorStr);
                            return refusal === CREDIT_REFUSAL.TEAM
                              ? i18n.t("InsufficientBalance.TeamTitle")
                              : refusal === CREDIT_REFUSAL.BALANCE
                                ? i18n.t("NoBalance")
                                : mapAiProviderErrorMessage(errorStr) || errorStr;
                          })()
                        : i18n.t("UnknownError") || "Error"}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="add-image-modal__ai-result-actions">
            <button
              type="button"
              className="add-image-modal__ai-result-btn add-image-modal__ai-result-btn--ghost"
              onClick={regenerateImage}
              disabled={resultEntries.some(([, r]) => r.status === "loading")}
            >
              {i18n.t("Regenerate") || "↩ Yeniden Oluştur"}
            </button>
          </div>
        </div>
        {renderInsufficientBalanceModal()}
      </>
    );
  }

  return (
    <>
      <div className="add-image-modal__ai-tab">
        <div className="add-image-modal__ai-section">
          <div className="add-image-modal__ai-label">
            {i18n.t("FileModal.APIProviders.ImageStyles.AIProvider")}
          </div>
          <div
            className="add-image-modal__ai-mode-toggle"
            role="tablist"
            aria-label={i18n.t("FileModal.APIProviders.ImageStyles.AIProvider")}
          >
            <button
              type="button"
              role="tab"
              aria-selected={isAutoProvider}
              className={`add-image-modal__ai-mode-toggle-btn${
                isAutoProvider ? " add-image-modal__ai-mode-toggle-btn--active" : ""
              }`}
              onClick={selectAutoMode}
            >
              <span>{i18n.t("FileModal.APIProviders.ImageStyles.AutoProvider") || "Otomatik"}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isAutoProvider}
              className={`add-image-modal__ai-mode-toggle-btn${
                !isAutoProvider ? " add-image-modal__ai-mode-toggle-btn--active" : ""
              }`}
              onClick={selectCustomMode}
            >
              <span>{i18n.t("FileModal.APIProviders.ImageStyles.CustomProvider") || "Özel"}</span>
            </button>
          </div>
          {!isAutoProvider && (
            <div className="add-image-modal__ai-provider-chips">
              {VISIBLE_IMAGE_STYLE_PROVIDER_OPTIONS.map((option) => {
                const isSelected = selectedProviders.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={option.disabled}
                    aria-disabled={option.disabled || undefined}
                    className={`add-image-modal__ai-provider-chip${isSelected ? " add-image-modal__ai-provider-chip--active" : ""}${option.disabled ? " add-image-modal__ai-provider-chip--disabled" : ""}`}
                    onClick={() => {
                      if (option.disabled) return;
                      toggleProvider(option.value);
                    }}
                    title={option.title || option.label}
                  >
                    {isSelected && <LuCheck size={11} />}
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {!isDirectMode && (
          <div className="add-image-modal__ai-section">
            <div className="add-image-modal__ai-label">
              {i18n.t("FileModal.APIProviders.ImageStyles.StyleLabel") || "Style"}
            </div>
            {stylesLoading && (
              <div className="add-image-modal__ai-style-empty">
                {i18n.t("FileModal.APIProviders.ImageStyles.LoadingStyles") || "Loading styles..."}
              </div>
            )}
            {stylesError && !stylesLoading && (
              <div className="add-image-modal__ai-style-empty">{stylesError}</div>
            )}
            {!stylesLoading && !stylesError && (
              <>
                <div className="add-image-modal__ai-style-grid">
                  {(showAllStyles ? styles : styles.slice(0, 5)).map((style, index) => {
                    const imageUrl = style.sampleImageUrl || style.thumbnail;
                    const label = resolveStyleLabel(style, index + 1);
                    return (
                      <button
                        key={style.id || index}
                        type="button"
                        onClick={() => {
                          clearReferenceImage();
                          setSelectedStyle(style.id);
                        }}
                        className={`add-image-modal__ai-style-card${
                          selectedStyle === style.id
                            ? " add-image-modal__ai-style-card--active"
                            : ""
                        }`}
                      >
                        {imageUrl ? <img src={imageUrl} alt="" /> : <span>{label}</span>}
                        <span>{label}</span>
                        {selectedStyle === style.id && (
                          <div className="add-image-modal__ai-style-check">
                            <LuCheck size={12} />
                          </div>
                        )}
                      </button>
                    );
                  })}

                  {/* Style Reference card */}
                  <input
                    ref={referenceFileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleReferenceImageSelect}
                  />
                  <button
                    type="button"
                    onClick={() => referenceFileInputRef.current?.click()}
                    className={`add-image-modal__ai-style-card add-image-modal__ai-style-card--custom${
                      hasReferenceImage ? " add-image-modal__ai-style-card--active" : ""
                    }`}
                    style={{ position: "relative", overflow: "hidden" }}
                  >
                    {hasReferenceImage && referenceImagePreview ? (
                      <img
                        src={referenceImagePreview}
                        alt="ref"
                        style={{
                          width: "100%",
                          height: "72px",
                          objectFit: "cover",
                          borderRadius: "10px",
                          position: "absolute",
                          top: "6px",
                          left: "6px",
                          right: "6px",
                          width: "calc(100% - 12px)",
                        }}
                      />
                    ) : (
                      <div className="add-image-modal__ai-style-custom-icon">
                        <LuImage size={24} />
                      </div>
                    )}
                    <span
                      style={{
                        position: "relative",
                        zIndex: 1,
                        marginTop: hasReferenceImage ? "78px" : "0",
                      }}
                    >
                      {i18n.t("FileModal.APIProviders.VideoGeneration.StyleReference") ||
                        "Style Reference"}
                    </span>
                    {hasReferenceImage && (
                      <div className="add-image-modal__ai-style-check">
                        <LuCheck size={12} />
                      </div>
                    )}
                  </button>

                  {/* Custom Style card — always last */}
                  <button
                    type="button"
                    onClick={() => {
                      clearReferenceImage();
                      setSelectedStyle(CUSTOM_STYLE_ID);
                    }}
                    className={`add-image-modal__ai-style-card add-image-modal__ai-style-card--custom${
                      isCustomStyle ? " add-image-modal__ai-style-card--active" : ""
                    }`}
                  >
                    <div className="add-image-modal__ai-style-custom-icon">
                      <LuPenLine size={24} />
                    </div>
                    <span>
                      {i18n.t("FileModal.APIProviders.ImageStyles.CustomCardLabel") || "Custom"}
                    </span>
                    {isCustomStyle && (
                      <div className="add-image-modal__ai-style-check">
                        <LuCheck size={12} />
                      </div>
                    )}
                  </button>
                </div>

                {isCustomStyle && (
                  <div className="add-image-modal__ai-custom-desc">
                    <label>
                      {i18n.t("FileModal.APIProviders.ImageStyles.CustomStyleDescription") ||
                        "Custom Style Description"}
                    </label>
                    <textarea
                      value={customStyleDesc}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value.length <= 2000) {
                          onCustomStyleDescChange?.(value);
                        }
                      }}
                      placeholder={
                        i18n.t("FileModal.APIProviders.ImageStyles.CustomStylePlaceholder") ||
                        "Describe the visual style you want, e.g. watercolor illustration, dark cinematic, flat vector art..."
                      }
                      rows={4}
                      maxLength={2000}
                    />
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#666",
                        marginTop: "4px",
                        textAlign: "right",
                      }}
                    >
                      {customStyleDesc.length}/2000
                    </div>
                  </div>
                )}

                {styles.length > 5 && (
                  <button
                    type="button"
                    className="add-image-modal__ai-style-more"
                    onClick={() => setShowAllStyles(!showAllStyles)}
                  >
                    <LuChevronDown
                      size={14}
                      style={{
                        transform: showAllStyles ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s",
                      }}
                    />
                    {showAllStyles
                      ? i18n.t("FileModal.APIProviders.ImageStyles.LessStyles") || "Daha Az Stil"
                      : i18n.t("FileModal.APIProviders.ImageStyles.MoreStyles") ||
                        "Daha Fazla Stil"}
                    {!showAllStyles && <span>{styles.length - 5}+</span>}
                  </button>
                )}
              </>
            )}
          </div>
        )}

        <div className="add-image-modal__ai-section">
          <div className="add-image-modal__ai-label">
            {i18n.t("OptionalEnhancements") || "Optional Enhancements"}
          </div>
          <div
            className="add-image-modal__ai-enhancements"
            style={{
              gridTemplateColumns: `repeat(${(isDirectMode ? 0 : 1) + 1 + 1}, minmax(0, 1fr))`,
            }}
          >
            {!isDirectMode && (
              <div
                className={`add-image-modal__ai-enhance-card${
                  textInImage ? " add-image-modal__ai-enhance-card--active" : ""
                }`}
              >
                <div className="add-image-modal__ai-enhance-info">
                  <div className="add-image-modal__ai-enhance-title">
                    {i18n.t("TextInImage") || "Text in Image"}
                  </div>
                  <div className="add-image-modal__ai-enhance-subtitle">
                    {i18n.t("OverlayText") || "Include visible text"}
                  </div>
                </div>
                <button
                  type="button"
                  className={`add-image-modal__ai-toggle${textInImage ? " add-image-modal__ai-toggle--on" : ""}`}
                  onClick={() => setTextInImage((prev) => !prev)}
                >
                  <span />
                </button>
              </div>
            )}

            <div className="add-image-modal__ai-enhance-card add-image-modal__ai-enhance-card--stacked">
              <div className="add-image-modal__ai-enhance-title">
                {i18n.t("AspectRatio") || "Aspect Ratio"}
              </div>
              <div className="add-image-modal__ai-ratio">
                {ASPECT_RATIOS.map((ratio) => {
                  const maxSide = Math.max(ratio.w, ratio.h);
                  const width = Math.max(12, Math.round((ratio.w / maxSide) * 22));
                  const height = Math.max(12, Math.round((ratio.h / maxSide) * 22));
                  return (
                    <button
                      key={ratio.id}
                      type="button"
                      onClick={() => onAspectRatioChange(ratio.id)}
                      className={`add-image-modal__ai-chip${
                        aspectRatio === ratio.id ? " add-image-modal__ai-chip--active" : ""
                      }`}
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "6px",
                        minHeight: "52px",
                      }}
                    >
                      <span
                        className="add-image-modal__ai-chip-box"
                        style={{
                          width,
                          height,
                          marginTop: "auto",
                        }}
                      />
                      <span style={{ marginTop: "auto" }}>{ratio.id}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className={`add-image-modal__ai-enhance-card add-image-modal__ai-enhance-card--stacked${
                resolutionSelectable ? "" : " add-image-modal__ai-enhance-card--disabled"
              }`}
            >
              <div className="add-image-modal__ai-enhance-title">
                {i18n.t("FileModal.APIProviders.ImageStyles.Resolution") || "Resolution"}
              </div>
              <div className="add-image-modal__ai-resolution">
                {["1K", "2K", "4K"].map((option) => (
                  <button
                    key={option}
                    type="button"
                    disabled={!resolutionSelectable}
                    aria-disabled={!resolutionSelectable || undefined}
                    className={`add-image-modal__ai-resolution-option${
                      resolution === option ? " active" : ""
                    }${resolutionSelectable ? "" : " add-image-modal__ai-resolution-option--disabled"}`}
                    onClick={() => {
                      if (!resolutionSelectable) return;
                      setResolution(option);
                    }}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {!isDirectMode && textInImage && (
            <div className="add-image-modal__ai-text-overlays">
              {textOverlays.map((overlay, index) => (
                <div key={overlay.id} className="add-image-modal__ai-overlay-item">
                  <div className="add-image-modal__ai-overlay-header">
                    <span>
                      {i18n.t("FileModal.APIProviders.ImageStyles.TextOverlayHeader", {
                        index: index + 1,
                      }) || `Text ${index + 1}`}
                    </span>
                    {textOverlays.length > 1 && (
                      <button
                        type="button"
                        className="add-image-modal__ai-overlay-remove"
                        onClick={() => removeOverlay(overlay.id)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <input
                    type="text"
                    className="add-image-modal__ai-overlay-input"
                    value={overlay.text}
                    onChange={(e) => updateOverlay(overlay.id, "text", e.target.value)}
                    placeholder={
                      i18n.t("FileModal.APIProviders.ImageStyles.TextOverlayPlaceholder") ||
                      "Enter the text to display..."
                    }
                  />
                  <div className="add-image-modal__ai-overlay-row">
                    <div className="add-image-modal__ai-overlay-field">
                      <label>
                        {i18n.t("FileModal.APIProviders.ImageStyles.TextOverlayLocationLabel") ||
                          "Location"}
                      </label>
                      <select
                        value={overlay.location}
                        onChange={(e) => updateOverlay(overlay.id, "location", e.target.value)}
                      >
                        {TEXT_LOCATIONS.map((loc) => (
                          <option key={loc.value} value={loc.value}>
                            {loc.label}
                          </option>
                        ))}
                        <option value="custom">
                          {i18n.t(
                            "FileModal.APIProviders.ImageStyles.TextOverlayLocationCustomOption"
                          ) || "Custom..."}
                        </option>
                      </select>
                      {overlay.location === "custom" && (
                        <input
                          type="text"
                          className="add-image-modal__ai-overlay-input"
                          value={overlay.customLocation}
                          onChange={(e) =>
                            updateOverlay(overlay.id, "customLocation", e.target.value)
                          }
                          placeholder={
                            i18n.t(
                              "FileModal.APIProviders.ImageStyles.TextOverlayLocationCustomPlaceholder"
                            ) || "e.g. lower third center"
                          }
                        />
                      )}
                    </div>
                    <div className="add-image-modal__ai-overlay-field">
                      <label>
                        {i18n.t("FileModal.APIProviders.ImageStyles.TextOverlayStyleLabel") ||
                          "Style"}
                      </label>
                      <select
                        value={overlay.style}
                        onChange={(e) => updateOverlay(overlay.id, "style", e.target.value)}
                      >
                        {TEXT_STYLES.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {overlay.style === "custom" && (
                    <input
                      type="text"
                      className="add-image-modal__ai-overlay-input"
                      value={overlay.customStyle}
                      onChange={(e) => updateOverlay(overlay.id, "customStyle", e.target.value)}
                      placeholder={
                        i18n.t(
                          "FileModal.APIProviders.ImageStyles.TextOverlayCustomStylePlaceholder"
                        ) || "e.g. bold red outlined"
                      }
                    />
                  )}
                </div>
              ))}
              {textOverlays.length < 5 && (
                <button
                  type="button"
                  className="add-image-modal__ai-overlay-add"
                  onClick={addOverlay}
                >
                  {i18n.t("FileModal.APIProviders.ImageStyles.AddTextOverlay") || "+ Add Text"}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="add-image-modal__ai-section">
          <div className="add-image-modal__ai-prompt-toggle">
            <button
              type="button"
              className={promptMode === "write" ? "active" : ""}
              onClick={() => {
                setPromptMode("write");
              }}
            >
              <LuPenLine /> {i18n.t("WritePrompt") || "Write Prompt"}
            </button>
            <button
              type="button"
              className={promptMode === "narration" ? "active" : ""}
              onClick={() => {
                setPromptMode("narration");
                const fallbackPrompt = narrationText || window.lastAiNarrationPrompt;
                if (fallbackPrompt && !narrationPrompt) {
                  setNarrationPrompt(fallbackPrompt);
                }
              }}
            >
              <LuFileText /> {i18n.t("FromNarration") || "From Narration"}
            </button>
            <button
              type="button"
              className={promptMode === "direct" ? "active" : ""}
              onClick={() => setPromptMode("direct")}
            >
              <LuPenLine /> {i18n.t("RawPrompt") || "Raw Prompt"}
            </button>
            {promptMode === "narration" && (
              <span className="add-image-modal__ai-prompt-badge">
                {selectedStyleLabel} · {aspectRatio} · {activeProviderLabel}
              </span>
            )}
          </div>

          {promptMode === "direct" ? (
            <>
              <textarea
                className="add-image-modal__ai-prompt-input"
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder={
                  i18n.t("FileModal.APIProviders.ImageStyles.DescribeImage") ||
                  "Describe the image you want to generate for this scene..."
                }
              />
              <div className="add-image-modal__ai-prompt-footer">
                <button
                  type="button"
                  className="add-image-modal__ai-enrich"
                  onClick={handleEnrichPrompt}
                  disabled={isEnrichingPrompt}
                  style={{
                    opacity: isEnrichingPrompt ? 0.6 : 1,
                    cursor: isEnrichingPrompt ? "not-allowed" : "pointer",
                  }}
                >
                  {isEnrichingPrompt
                    ? i18n.t("Enriching") || "⏳ Enriching..."
                    : i18n.t("EnrichMyPrompt") || "✨ Enrich my prompt"}
                </button>
                <span>
                  {aspectRatio} · {activeProviderLabel}
                </span>
              </div>
            </>
          ) : promptMode === "write" ? (
            <>
              <textarea
                className="add-image-modal__ai-prompt-input"
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder={
                  i18n.t("FileModal.APIProviders.ImageStyles.DescribeImage") ||
                  "Describe the image you want to generate for this scene..."
                }
              />
              <div className="add-image-modal__ai-prompt-footer">
                <button
                  type="button"
                  className="add-image-modal__ai-enrich"
                  onClick={handleEnrichPrompt}
                  disabled={isEnrichingPrompt}
                  style={{
                    opacity: isEnrichingPrompt ? 0.6 : 1,
                    cursor: isEnrichingPrompt ? "not-allowed" : "pointer",
                  }}
                >
                  {isEnrichingPrompt
                    ? i18n.t("Enriching") || "⏳ Enriching..."
                    : i18n.t("EnrichMyPrompt") || "✨ Enrich my prompt"}
                </button>
                <span>
                  {selectedStyleLabel} · {aspectRatio} · {activeProviderLabel}
                </span>
              </div>
            </>
          ) : (
            <>
              <div className="add-image-modal__ai-narration">
                <div className="add-image-modal__ai-narration-header">
                  <span>
                    <LuFileText /> {i18n.t("SceneNarration") || "Scene Narration"}
                  </span>
                  <span>
                    {i18n.t("EditToHighlightKeyVisuals") || "Edit to highlight key visuals"}
                  </span>
                </div>
                <textarea
                  className="add-image-modal__ai-narration-input"
                  value={prompt}
                  onChange={(event) => setPrompt(event.target.value)}
                  placeholder={
                    i18n.t("DescribeSceneNarration") || "Describe the scene narration..."
                  }
                />
                <div className="add-image-modal__ai-narration-hint">
                  <LuLightbulb />{" "}
                  {i18n.t("TipRemoveParts") ||
                    "Tip: Remove parts that don't need visual support. Keep the sentences describing what should be shown."}
                </div>
              </div>
              <button
                type="button"
                className="add-image-modal__ai-narration-cta"
                onClick={handleGeneratePromptFromNarration}
                disabled={isGeneratingFromNarration}
                style={{
                  opacity: isGeneratingFromNarration ? 0.6 : 1,
                  cursor: isGeneratingFromNarration ? "not-allowed" : "pointer",
                }}
              >
                <LuLightbulb />{" "}
                {isGeneratingFromNarration
                  ? i18n.t("Generating") || "⏳ Generating..."
                  : i18n.t("GenerateImagePromptFromNarration") ||
                    "Generate Image Prompt from Narration"}
              </button>
            </>
          )}
        </div>

        {!isDirectMode && (
          <div className="add-image-modal__ai-section">
            <button
              type="button"
              className="add-image-modal__ai-advanced-toggle"
              onClick={() => setAdvancedOpen((prev) => !prev)}
            >
              {advancedOpen ? "▾" : "▸"}{" "}
              {i18n.t("FileModal.APIProviders.ImageStyles.AdvancedSettings") || "Advanced Settings"}
            </button>
            {advancedOpen && (
              <div className="add-image-modal__ai-advanced-card">
                <label>
                  {i18n.t("FileModal.APIProviders.ImageStyles.NegativePrompt") || "Negative Prompt"}
                </label>
                <input
                  type="text"
                  value={negativePrompt}
                  onChange={(event) => setNegativePrompt(event.target.value)}
                  placeholder={
                    i18n.t("FileModal.APIProviders.ImageStyles.NegativePromptPlaceholder") ||
                    "What to exclude..."
                  }
                />
              </div>
            )}
          </div>
        )}
      </div>
      {renderInsufficientBalanceModal()}
    </>
  );
};

export default AIImageGenerateTab;
